'use client';

import { twMerge } from 'tailwind-merge';

import { useFloatingLayer } from '@/shared/contexts/FloatingLayerContext';

import { LeadBar } from './LeadBar';
import { LeadButton } from './LeadButton';
import { ScrollToTopButton } from './ScrollToTopButton';
import { ZentoButton } from './ZentoButton';

/** 스크롤락 시 `--scrollbar-lock-width` 만큼 left 보정 (Header·popup overlay 와 동일) */
const SCROLL_LOCK_RIGHT =
  'desktop:right-[calc(20px+var(--scrollbar-lock-width,0px))]';

/**
 * Lead 그룹 래퍼 클래스
 * - MO: 화면 하단 full-width 바
 * - PC: 우측 세로 중앙 레일 (LeadButton 단독)
 */
const BASE_LEAD_WRAPPER = `pointer-events-none fixed inset-x-0 bottom-0 z-40 flex desktop:left-auto desktop:bottom-auto desktop:top-1/2 desktop:-translate-y-1/2 desktop:flex-col desktop:items-center ${SCROLL_LOCK_RIGHT}`;

/**
 * 젠투 래퍼 클래스 — 젠투 버튼 + 그 아래 ScrollTop 앵커 스택 (MO/PC 공통 구조).
 * - MO: 우하단, px-20/pb-20, gap-12
 * - PC: 우하단 bottom-56, right-20, gap-16 (앵커는 젠투 버튼 기준 가운데 정렬)
 */
const BASE_ZENTO_WRAPPER = `pointer-events-none fixed right-0 bottom-0 z-40 flex flex-col items-center gap-12 px-20 pb-20 desktop:bottom-56 desktop:gap-16 desktop:p-0 ${SCROLL_LOCK_RIGHT}`;

/**
 * 전역 플로팅 CTA 렌더러 — `AppProviders` 하위에 한 번만 마운트.
 *
 * 두 개의 독립 wrapper:
 * - Lead 그룹: MO 하단 바 / PC 세로 중앙 (LeadButton)
 * - Zento: 하단 고정. 젠투 버튼 + ScrollTop 을 함께 스택하고, MO 에서 Lead 바가 있으면 그 위로 올린다.
 *   페이지가 자체 하단 CTA 를 띄우면(`pageBottomCta`) Lead 바 대신 그 CTA 위로 올라간다.
 *
 * 각 wrapper 는 `leadClassName` / `zentoClassName` 로 축별 override 가능.
 * `pointer-events-none` wrapper + 각 버튼 `pointer-events-auto` 로 배경 클릭 방해 없음.
 * z-40: 모달(z-100+) 아래, LNB(z-50+) 아래, 일반 콘텐츠 위.
 */
export function FloatingLayer() {
  const { lead, scrollTop, zento, container, pageBottomCta } =
    useFloatingLayer();

  const showLead = !!lead?.visible;
  // ScrollTop 도 젠투와 함께 기본 노출 — slot 미등록(=FloatingConfig 없음) 이면 켜진 상태.
  const showScrollTop = scrollTop?.visible ?? true;
  // Zento 는 slot 미등록(=FloatingConfig 없음) 상황에서도 기본 노출.
  // 명시적으로 `{ visible: false }` 이거나 buttonMode 가 custom 이 아니면 프로젝트 버튼 미노출.
  const showZento =
    (zento?.visible ?? true) && (zento?.buttonMode ?? 'custom') === 'custom';

  // MO 하단 바는 전화 단독 구성이면 노출할 항목이 없다 (전화는 MO 미노출).
  // 페이지 자체 하단 CTA 가 있으면 같은 자리를 쓰므로 바는 접는다 (PC 세로 레일은 유지).
  const showLeadBar =
    showLead &&
    !pageBottomCta &&
    (!!lead?.render || !!lead?.estimate || !!lead?.consult);

  return (
    <>
      {showLead && (
        <div
          className={twMerge(
            BASE_LEAD_WRAPPER,
            // Lead 바가 없으면 MO 에서 래퍼 자체를 접는다 (ScrollTop 은 젠투 스택에 있음).
            !showLeadBar && 'desktop:flex hidden',
            container?.leadClassName,
          )}
        >
          <div className="desktop:w-auto pointer-events-auto w-full">
            {lead.render ? (
              lead.render()
            ) : (
              <>
                <LeadBar
                  showEstimate={lead.estimate}
                  showConsult={lead.consult}
                  consultAction={lead.consultAction}
                />
                <LeadButton
                  showEstimate={lead.estimate}
                  showConsult={lead.consult}
                  showCall={lead.call}
                  consultAction={lead.consultAction}
                />
              </>
            )}
          </div>
        </div>
      )}
      {(showZento || showScrollTop) && (
        <div
          className={twMerge(
            BASE_ZENTO_WRAPPER,
            // MO 에서 하단 리드 바(h-56) 위로 띄운다. PC 는 원래 bottom-56.
            showLeadBar && 'bottom-56',
            // 페이지 자체 하단 CTA 위로 띄운다 (MO 는 pb-20 포함해 하단 120, PC 는 108).
            pageBottomCta && 'desktop:bottom-108 bottom-100',
            container?.zentoClassName,
          )}
        >
          {/* inline 요소의 baseline 여백이 gap-12 를 밀지 않도록 flex 로 감싼다 (custom render 대응) */}
          {showZento && (
            <div className="pointer-events-auto flex">
              {zento?.render ? zento.render() : <ZentoButton />}
            </div>
          )}
          {showScrollTop && (
            <div className="pointer-events-auto flex">
              <ScrollToTopButton />
            </div>
          )}
        </div>
      )}
    </>
  );
}
